import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  Activity,
  Users,
  Upload,
  Clock,
  Calendar,
  Search,
  Download,
  RefreshCw,
  LogIn,
  FileUp,
  Eye,
  Filter,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  TrendingUp,
  Shield,
  Globe,
  Zap,
  Layers,
  Timer,
  UserCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DMYDateInput } from "@/components/ui/dmy-date-picker";
import { formatDMY } from "@/lib/dmy";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LogsPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  // State
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [loginLogs, setLoginLogs] = useState([]);
  const [uploadLogs, setUploadLogs] = useState([]);
  const [userSessions, setUserSessions] = useState([]);
  const [activityTimeline, setActivityTimeline] = useState([]);
  const [allLogs, setAllLogs] = useState([]);
  
  // Filters
  const [dateRange, setDateRange] = useState(7);
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  
  // Pagination
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 50;

  // Check admin access
  useEffect(() => {
    if (user?.role !== "super_admin" && user?.role !== "admin") {
      navigate("/");
    }
  }, [user, navigate]);

  // Fetch data based on active tab
  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      };
      
      switch (activeTab) {
        case "dashboard":
          const dashRes = await fetch(`${API_URL}/api/logs/dashboard?days=${dateRange}`, { headers });
          if (dashRes.ok) {
            const data = await dashRes.json();
            setDashboardData(data);
          }
          break;
          
        case "logins":
          let loginUrl = `${API_URL}/api/logs/logins?limit=${pageSize}&skip=${page * pageSize}`;
          if (fromDate) loginUrl += `&from_date=${fromDate}`;
          if (toDate) loginUrl += `&to_date=${toDate}`;
          if (selectedUser) loginUrl += `&username=${selectedUser}`;
          
          const loginRes = await fetch(loginUrl, { headers });
          if (loginRes.ok) {
            const data = await loginRes.json();
            setLoginLogs(data.logins || []);
            setTotalItems(data.total || 0);
          }
          break;
          
        case "uploads":
          let uploadUrl = `${API_URL}/api/logs/file-uploads?limit=${pageSize}&skip=${page * pageSize}`;
          if (fromDate) uploadUrl += `&from_date=${fromDate}`;
          if (toDate) uploadUrl += `&to_date=${toDate}`;
          if (selectedUser) uploadUrl += `&username=${selectedUser}`;
          
          const uploadRes = await fetch(uploadUrl, { headers });
          if (uploadRes.ok) {
            const data = await uploadRes.json();
            setUploadLogs(data.uploads || []);
            setTotalItems(data.total || 0);
          }
          break;
          
        case "sessions":
          const sessionRes = await fetch(`${API_URL}/api/logs/user-sessions?days=${dateRange}`, { headers });
          if (sessionRes.ok) {
            const data = await sessionRes.json();
            setUserSessions(data.sessions || []);
          }
          break;
          
        case "timeline":
          const timelineRes = await fetch(`${API_URL}/api/logs/activity-timeline?hours=48`, { headers });
          if (timelineRes.ok) {
            const data = await timelineRes.json();
            setActivityTimeline(data.activities || []);
          }
          break;
          
        case "all":
          let allUrl = `${API_URL}/api/activity-logs?limit=${pageSize}&skip=${page * pageSize}`;
          if (fromDate) allUrl += `&from_date=${fromDate}`;
          if (toDate) allUrl += `&to_date=${toDate}`;
          if (selectedUser) allUrl += `&username=${selectedUser}`;
          if (searchTerm) allUrl += `&action=${searchTerm}`;
          
          const allRes = await fetch(allUrl, { headers });
          if (allRes.ok) {
            const data = await allRes.json();
            setAllLogs(data.logs || []);
            setTotalItems(data.total || 0);
          }
          break;
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLoading(false);
    }
  }, [token, activeTab, dateRange, page, fromDate, toDate, selectedUser, searchTerm]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Export logs
  const handleExport = async (format = "csv") => {
    if (!fromDate || !toDate) {
      alert("Please select date range for export");
      return;
    }
    
    try {
      const response = await fetch(
        `${API_URL}/api/logs/export?from_date=${fromDate}&to_date=${toDate}&format=${format}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `logs_${fromDate}_to_${toDate}.${format}`;
        a.click();
      }
    } catch (error) {
      console.error("Export error:", error);
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return "-";
    try {
      const date = new Date(timestamp);
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return timestamp;
    }
  };

  // Render the `details` field safely. Some legacy log entries store
  // `details` as a dict (e.g. {file_name, status, rows} from raw_funds /
  // partner_ledger uploads); React throws "Objects are not valid as a
  // React child" if such an object is placed directly in JSX. This helper
  // turns objects into a human-readable inline summary.
  const renderLogDetails = (val) => {
    if (val == null || val === "") return "-";
    if (typeof val === "string") return val;
    if (typeof val === "number" || typeof val === "boolean") return String(val);
    if (typeof val === "object") {
      try {
        // Prefer a compact "key=val" listing of scalar fields; fall back to JSON.
        const parts = [];
        for (const [k, v] of Object.entries(val)) {
          if (v === null || v === undefined) continue;
          const pretty = typeof v === "object" ? JSON.stringify(v) : String(v);
          if (pretty.length > 80) continue;  // skip long blobs
          parts.push(`${k}=${pretty}`);
        }
        if (parts.length) return parts.join(" · ");
        return JSON.stringify(val);
      } catch {
        return "[unserialisable]";
      }
    }
    return String(val);
  };

  // Get action badge color
  const getActionColor = (action) => {
    const colors = {
      login: "bg-green-100 text-green-700 border-green-200",
      logout: "bg-slate-100 text-slate-700 border-slate-200",
      upload: "bg-blue-100 text-blue-700 border-blue-200",
      create: "bg-emerald-100 text-emerald-700 border-emerald-200",
      update: "bg-amber-100 text-amber-700 border-amber-200",
      delete: "bg-red-100 text-red-700 border-red-200",
      view: "bg-purple-100 text-purple-700 border-purple-200",
      export: "bg-cyan-100 text-cyan-700 border-cyan-200"
    };

    const key = Object.keys(colors).find(k => action?.toLowerCase().includes(k));
    return colors[key] || "bg-slate-100 text-slate-700 border-slate-200";
  };

  // Get role badge
  const getRoleBadge = (role) => {
    const styles = {
      super_admin: "bg-purple-100 text-purple-700",
      admin: "bg-blue-100 text-blue-700",
      partner: "bg-green-100 text-green-700",
      agent: "bg-amber-100 text-amber-700"
    };
    return styles[role] || "bg-slate-100 text-slate-700";
  };

  // Render Dashboard Tab
  const renderDashboard = () => {
    if (!dashboardData) return <div className="text-center py-8 text-slate-500">Loading dashboard...</div>;
    
    const { summary, by_module, by_user, by_day, by_action, recent_logins } = dashboardData;
    
    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Total Logins</p>
                  <p className="text-3xl font-bold">{summary.total_logins}</p>
                </div>
                <LogIn className="w-10 h-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">Unique Users</p>
                  <p className="text-3xl font-bold">{summary.unique_users}</p>
                </div>
                <Users className="w-10 h-10 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">File Uploads</p>
                  <p className="text-3xl font-bold">{summary.total_uploads}</p>
                </div>
                <Upload className="w-10 h-10 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Total Activities</p>
                  <p className="text-3xl font-bold">{summary.total_activities}</p>
                </div>
                <Activity className="w-10 h-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Activity by Day Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-500" />
                Activity Trend
              </CardTitle>
              <CardDescription>Daily activity over last {dateRange} days</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-48 flex items-end gap-1">
                {by_day.map((day, idx) => {
                  const maxCount = Math.max(...by_day.map(d => d.count), 1);
                  const height = (day.count / maxCount) * 100;
                  return (
                    <TooltipProvider key={idx}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div 
                            className="flex-1 bg-gradient-to-t from-blue-500 to-blue-400 rounded-t hover:from-blue-600 hover:to-blue-500 transition-colors cursor-pointer"
                            style={{ height: `${Math.max(height, 2)}%` }}
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="font-medium">{formatDMY(day.date)}</p>
                          <p className="text-sm">{day.count} activities</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          
          {/* Top Users */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
                Most Active Users
              </CardTitle>
              <CardDescription>By number of actions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {by_user.slice(0, 5).map((user, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-sm font-bold text-slate-600">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{user.username}</span>
                        <Badge variant="outline" className={`text-xs ${getRoleBadge(user.role)}`}>
                          {user.role}
                        </Badge>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full mt-1 overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full transition-all"
                          style={{ width: `${(user.count / (by_user[0]?.count || 1)) * 100}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-sm font-medium text-slate-600">{user.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* By Module */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Layers className="w-5 h-5 text-purple-500" />
                By Module
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {by_module.slice(0, 6).map((mod, idx) => (
                  <div key={idx} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                    <span className="text-sm text-slate-600">{mod.module || "General"}</span>
                    <Badge variant="secondary">{mod.count}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          
          {/* By Action */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                By Action Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {by_action.slice(0, 10).map((action, idx) => (
                  <Badge key={idx} variant="outline" className={getActionColor(action.action)}>
                    {action.action} ({action.count})
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
          
          {/* Recent Logins */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-green-500" />
                Recent Logins
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {recent_logins.slice(0, 8).map((login, idx) => (
                  <div key={idx} className="flex items-center justify-between py-1.5 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-400"></div>
                      <span className="font-medium">{login.username}</span>
                    </div>
                    <span className="text-xs text-slate-500">{formatTime(login.timestamp)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  // Render Login Logs Tab
  const renderLoginLogs = () => (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">User</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Login Time</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Duration</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">IP Address</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loginLogs.map((log, idx) => (
                  <tr key={idx} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold">
                          {log.username?.charAt(0)?.toUpperCase()}
                        </div>
                        <span className="font-medium">{log.username}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={getRoleBadge(log.role)}>
                        {log.role}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{formatTime(log.timestamp)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <Timer className="w-4 h-4 text-slate-400" />
                        <span className="text-sm font-medium">{log.estimated_duration || "-"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-sm text-slate-500">
                        <Globe className="w-4 h-4" />
                        {log.ip_address || "-"}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Render File Uploads Tab
  const renderUploadLogs = () => (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">User</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Details</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Module</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {uploadLogs.map((log, idx) => (
                  <tr key={idx} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center text-white text-xs font-bold">
                          <FileUp className="w-4 h-4" />
                        </div>
                        <span className="font-medium">{log.username}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={getActionColor(log.action)}>
                        {log.action}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate" title={typeof log.details === "object" ? JSON.stringify(log.details) : (log.details || "")}>
                      {renderLogDetails(log.details)}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="secondary">{log.module || "General"}</Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">{formatTime(log.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Render User Sessions Tab
  const renderUserSessions = () => (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">User</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Logins</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Actions</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Time Spent</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Modules</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Last Active</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {userSessions.map((session, idx) => (
                  <tr key={idx} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                          {session.username?.charAt(0)?.toUpperCase()}
                        </div>
                        <span className="font-medium">{session.username}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={getRoleBadge(session.role)}>
                        {session.role}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <LogIn className="w-4 h-4 text-green-500" />
                        <span className="font-medium">{session.login_count}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-blue-600">{session.total_actions}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-4 h-4 text-amber-500" />
                        <span className="font-medium">{session.estimated_time_spent}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {session.modules_accessed?.slice(0, 3).map((mod, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {mod}
                          </Badge>
                        ))}
                        {session.modules_accessed?.length > 3 && (
                          <Badge variant="secondary" className="text-xs">
                            +{session.modules_accessed.length - 3}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {formatTime(session.last_activity)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Render Activity Timeline Tab
  const renderActivityTimeline = () => (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Live Activity Feed</CardTitle>
          <CardDescription>Last 48 hours of activity</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {activityTimeline.map((activity, idx) => (
              <div key={idx} className="flex gap-4 pb-3 border-b border-slate-100 last:border-0">
                <div className="flex-shrink-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getActionColor(activity.action)}`}>
                    {activity.action?.toLowerCase().includes("login") ? (
                      <LogIn className="w-5 h-5" />
                    ) : activity.action?.toLowerCase().includes("upload") ? (
                      <Upload className="w-5 h-5" />
                    ) : activity.action?.toLowerCase().includes("view") ? (
                      <Eye className="w-5 h-5" />
                    ) : (
                      <Activity className="w-5 h-5" />
                    )}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{activity.username}</span>
                    <Badge variant="outline" className={getRoleBadge(activity.role)}>
                      {activity.role}
                    </Badge>
                    <Badge variant="outline" className={getActionColor(activity.action)}>
                      {activity.action}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 mt-1 truncate">{activity.details}</p>
                  {activity.module && (
                    <Badge variant="secondary" className="mt-1 text-xs">
                      {activity.module}
                    </Badge>
                  )}
                </div>
                <div className="flex-shrink-0 text-xs text-slate-500">
                  {formatTime(activity.timestamp)}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Render All Logs Tab
  const renderAllLogs = () => (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">User</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Details</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Module</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">IP</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {allLogs.map((log, idx) => (
                  <tr key={idx} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{log.username}</span>
                        <Badge variant="outline" className={`text-xs ${getRoleBadge(log.role)}`}>
                          {log.role}
                        </Badge>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={getActionColor(log.action)}>
                        {log.action}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate" title={typeof log.details === "object" ? JSON.stringify(log.details) : (log.details || "")}>
                      {renderLogDetails(log.details)}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="secondary">{log.module || "-"}</Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">{log.ip_address || "-"}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">{formatTime(log.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl text-white">
                <Activity className="w-6 h-6" />
              </div>
              Activity Logs
            </h1>
            <p className="text-slate-500 mt-1">Monitor user activity, logins, and system events</p>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            {/* Date Range Select */}
            <Select value={dateRange.toString()} onValueChange={(v) => setDateRange(parseInt(v))}>
              <SelectTrigger className="w-36">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last 24h</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Refresh Button */}
            <Button variant="outline" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            
            {/* Export Button */}
            <Button variant="outline" onClick={() => handleExport("csv")}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white border shadow-sm p-1 h-auto flex-wrap">
          <TabsTrigger value="dashboard" className="gap-2 data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
            <BarChart3 className="w-4 h-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="logins" className="gap-2 data-[state=active]:bg-green-50 data-[state=active]:text-green-700">
            <LogIn className="w-4 h-4" />
            Logins
          </TabsTrigger>
          <TabsTrigger value="uploads" className="gap-2 data-[state=active]:bg-amber-50 data-[state=active]:text-amber-700">
            <Upload className="w-4 h-4" />
            File Uploads
          </TabsTrigger>
          <TabsTrigger value="sessions" className="gap-2 data-[state=active]:bg-purple-50 data-[state=active]:text-purple-700">
            <Clock className="w-4 h-4" />
            User Sessions
          </TabsTrigger>
          <TabsTrigger value="timeline" className="gap-2 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
            <Activity className="w-4 h-4" />
            Timeline
          </TabsTrigger>
          <TabsTrigger value="all" className="gap-2 data-[state=active]:bg-slate-100 data-[state=active]:text-slate-700">
            <Layers className="w-4 h-4" />
            All Logs
          </TabsTrigger>
        </TabsList>
        
        {/* Filters (for non-dashboard tabs) */}
        {activeTab !== "dashboard" && activeTab !== "sessions" && (
          <Card className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <span className="text-sm font-medium text-slate-600">Filters:</span>
              </div>
              
              <DMYDateInput placeholder="From Date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-40" />
              
              <DMYDateInput placeholder="To Date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-40" />
              
              <Input
                placeholder="Username..."
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="w-40"
              />
              
              {activeTab === "all" && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search action..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 w-48"
                  />
                </div>
              )}
              
              <Button variant="ghost" size="sm" onClick={() => {
                setFromDate("");
                setToDate("");
                setSelectedUser("");
                setSearchTerm("");
              }}>
                Clear
              </Button>
            </div>
          </Card>
        )}
        
        {/* Tab Content */}
        <TabsContent value="dashboard" className="mt-0">
          {renderDashboard()}
        </TabsContent>
        
        <TabsContent value="logins" className="mt-0">
          {renderLoginLogs()}
        </TabsContent>
        
        <TabsContent value="uploads" className="mt-0">
          {renderUploadLogs()}
        </TabsContent>
        
        <TabsContent value="sessions" className="mt-0">
          {renderUserSessions()}
        </TabsContent>
        
        <TabsContent value="timeline" className="mt-0">
          {renderActivityTimeline()}
        </TabsContent>
        
        <TabsContent value="all" className="mt-0">
          {renderAllLogs()}
        </TabsContent>
      </Tabs>
      
      {/* Pagination (for tabular views) */}
      {(activeTab === "logins" || activeTab === "uploads" || activeTab === "all") && totalItems > 0 && (
        <div className="flex items-center justify-between mt-4 bg-white rounded-lg border p-3">
          <span className="text-sm text-slate-600">
            Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, totalItems)} of {totalItems}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * pageSize >= totalItems}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogsPage;
